import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { MultiSelectField, SocialMediaLinkInput } from 'components/Form';
import {
  iconFBLink,
  iconIGLink,
  iconLinkedInLink,
  iconSpotifyLink,
  iconYoutubeLink,
} from 'assets';
import { isCreativeProfile } from '../config';

const SOCIAL_FIELDS = [
  {
    key: 'linked_in',
    image: iconLinkedInLink.default,
    placeholder: 'Paste LinkedIn link here...',
  },
  {
    key: 'instagram',
    image: iconIGLink.default,
    placeholder: 'Paste Instagram link here...',
  },
  {
    key: 'facebook',
    image: iconFBLink.default,
    placeholder: 'Paste Facebook link here...',
  },
  {
    key: 'spotify_playlists',
    name: 'spotify_playlists.0',
    image: iconSpotifyLink.default,
    placeholder: 'Paste Spotify link here...',
  },
  {
    key: 'youtube_playlists',
    name: 'youtube_playlists.0',
    image: iconYoutubeLink.default,
    placeholder: 'Paste YouTube link here...',
  },
];

const CREATIVE_SOCIAL_FIELDS = ['spotify_playlists', 'youtube_playlists'];

const ConnectStep = ({
  classificationOptions,
  fieldKeys,
  isLoadingClassifications,
}) => {
  const { values } = useFormikContext();
  const isCreative = isCreativeProfile({
    industry_areas: values.industry_areas,
  });
  const show = (key) =>
    fieldKeys.includes(key) &&
    (!CREATIVE_SOCIAL_FIELDS.includes(key) || isCreative);
  const hasSocialFields = SOCIAL_FIELDS.some(({ key }) => show(key));

  return (
    <div>
      {hasSocialFields && (
        <div className="bg-twilight rounded-lg p-5 mb-6">
          <p className="font-semibold text-ebony mb-1">Social links</p>
          <p className="text-xs text-dusty">
            Add any missing profiles that help members learn more about you.
          </p>
        </div>
      )}
      <div className="flex flex-col">
        {SOCIAL_FIELDS.filter(({ key }) => show(key)).map(
          ({ image, key, name, placeholder }) => (
            <SocialMediaLinkInput
              className="w-full"
              key={key}
              name={name || key}
              img={image}
              placeholder={placeholder}
            />
          )
        )}
      </div>
      {show('music_genres') && (
        <MultiSelectField
          name="music_genres"
          label="Content topics you are interested in"
          options={classificationOptions.music_genres}
          placeholder="Select content topics"
          creatable={false}
          isLoading={isLoadingClassifications}
        />
      )}
      {show('event_types') && (
        <MultiSelectField
          name="event_types"
          label="Event types you are interested in (up to 3)"
          max={3}
          options={classificationOptions.event_types}
          placeholder="Select event types"
          creatable={false}
          isLoading={isLoadingClassifications}
        />
      )}
      {show('event_formats') && (
        <MultiSelectField
          name="event_formats"
          label="Preferred event formats"
          options={classificationOptions.event_formats}
          placeholder="Select event formats"
          creatable={false}
          isLoading={isLoadingClassifications}
        />
      )}
      {show('event_attend_locations') && (
        <MultiSelectField
          name="event_attend_locations"
          label="Where would you attend events?"
          options={classificationOptions.event_attend_locations}
          placeholder="Select event regions"
          creatable={false}
          isLoading={isLoadingClassifications}
        />
      )}
    </div>
  );
};

ConnectStep.propTypes = {
  classificationOptions: PropTypes.object.isRequired,
  fieldKeys: PropTypes.array.isRequired,
  isLoadingClassifications: PropTypes.bool,
};

ConnectStep.defaultProps = {
  isLoadingClassifications: false,
};

export default ConnectStep;
